import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Form, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import PropTypes from 'prop-types';

import { requestDownload, requestUpload } from 'api/sfms/product/output';
import Divider from 'components/common/Divider';
import AccordionButton from 'components/common-sfims/button/Accordion';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onInputChange } from 'function/event';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, KOREA_BU } from 'types/const';
import { RETURN_YN } from 'types/type';

/**
 * 배합비 작성 정보 관리 > 제품 생산량 - 업로드
 */
const Upload = ({ show, onHide, onUpload }) => {
  /* variable */
  const { adminYn, fatoryCodeSeq } = getLoginInform();
  const accept =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { hasMods: hasModPlants }
  } = usePlantState();

  /* reference */
  const fileEl = useRef(null);

  /* state */
  // 업로드 비활성화 여부
  const [isDisableUpload, setIsDisableUpload] = useState(false);
  // 제품 생산량 업로드 요청 데이터
  const [uploadData, setUploadData] = useObjReducer({});

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우 데이터 초기화
    if (show) {
      setUploadData({
        factoryCodeSeq: fatoryCodeSeq,
        prodYm: moment()
          .add(moment().date() >= 22 ? 1 : 0, 'months')
          .format('YYYYMM'),
        file: null
      });
    }
  }, [show]);

  /* event */
  // 파일 변경 시
  const onChangeFile = ({ target: { files } }) => {
    const [file] = files;

    try {
      if (file.size > 50000000) {
        throw 'fileTooLarge';
      }

      if (accept !== file.type) {
        throw 'notAcceptFileType';
      }

      setUploadData({ file });
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: error
      });

      fileEl.current.value = '';
    }
  };
  // 폼 다운로드 클릭 시
  const onDownloadFormClick = async () => {
    try {
      const { data, name } = await requestDownload();

      downloadFile(data, name);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    }
  };
  // 업로드
  const onSubmit = async event => {
    let title = 'complete';
    let message = 'completeUpload';

    event.preventDefault();

    setIsDisableUpload(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(uploadData)) {
        throw { code: RESPONSE_CODE.BadRequestInsert };
      }

      const { code, message: uploadMessage } = await requestUpload(uploadData);

      if (code !== RESPONSE_CODE.Success) {
        throw { code: 'failureUpload' };
      }

      if (uploadMessage) {
        message = uploadMessage;
      }

      // 업로드 시 이벤트 실행
      onUpload();

      // 모달 숨김
      onHide();
    } catch (error) {
      title = 'failure';
      message = getErrorMessage(error?.code, error?.error);
    } finally {
      setConfirmModal({
        isOpen: true,
        title,
        message
      });
      setIsDisableUpload(false);
    }
  };

  /* view */
  return (
    <Modal show={show} onHide={onHide}>
      <Fragment key="header">{getText('upload')}</Fragment>
      <Fragment key="body">
        <Form onSubmit={onSubmit}>
          <Form.Group>
            <InputGroup className="mb-3">
              <InputGroup.Text className="w-25 fs--1">
                * {getText('selectFactory')}
              </InputGroup.Text>
              <Select
                datas={array2ValueAndText(
                  hasModPlants.filter(({ buCode } = {}) => buCode !== KOREA_BU),
                  'seq',
                  'pltCode'
                )}
                placeholder="selectFactory"
                value={uploadData.factoryCodeSeq}
                name="factoryCodeSeq"
                required
                onChange={event => onInputChange(event, setUploadData)}
              />
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Text className="w-25 fs--1">
                * {getText('selectYm')}
              </InputGroup.Text>
              <MonthPicker
                value={uploadData.prodYm}
                placeholderText={getText('selectYm')}
                wrapperClassName="w-75"
                onUpdate={prodYm => setUploadData({ prodYm })}
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>* {getText('selectFileExcel')}</Form.Label>
            <Form.Control
              ref={fileEl}
              size="sm"
              type="file"
              accept={accept}
              required
              onChange={onChangeFile}
            />
          </Form.Group>
          <div className="d-flex flex-row align-items-center justify-content-center">
            <Button variant="secondary" onClick={onHide}>
              {getText('cancel')}
            </Button>
            <Button disabled={isDisableUpload} type="submit" className="ms-2">
              {getText('upload')}
            </Button>
          </div>
          <Divider />
          <div className="d-flex flex-row align-items-center">
            <p className="fs--1 text-danger mb-0">
              {getText('downloadFormMessage')}
            </p>
            <Button size="sm" className="ms-auto" onClick={onDownloadFormClick}>
              {getText('downloadForm')}
            </Button>
          </div>
          {/* TODO */}
          {/* {adminYn === RETURN_YN.Y && (
            <Accordion>
              <AccordionButton eventKey="0">
                {getText('uploadForm')}
              </AccordionButton>
              <Accordion.Collapse eventKey="0">
                <Form.Group className="mb-3 mt-3">
                  <Form.Label>* {getText('selectFileExcel')}</Form.Label>
                  <Form.Control
                    size="sm"
                    type="file"
                    accept={accept}
                    className="mb-3"
                  />
                  <div className="d-flex flex-row align-items-center justify-content-center">
                    <Button>{getText('upload')}</Button>
                  </div>
                </Form.Group>
              </Accordion.Collapse>
            </Accordion>
          )} */}
        </Form>
      </Fragment>
    </Modal>
  );
};

Upload.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 업로드 시 이벤트
  onUpload: PropTypes.func
};
Upload.defaultProps = { show: false };

export default Upload;
